/** OAuth 2.0 PKCE utilities */

const PKCE_STATE_KEY = 'oauth_pkce_state';

// ── PKCE helpers ─────────────────────────────────────────────────────────────

function randomBase64Url(byteLength: number): string {
  const arr = new Uint8Array(byteLength);
  crypto.getRandomValues(arr);
  return btoa(String.fromCharCode(...Array.from(arr)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

async function sha256Base64Url(plain: string): Promise<string> {
  const data = new TextEncoder().encode(plain);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...Array.from(new Uint8Array(digest))))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

export interface PkceState {
  provider: string;
  codeVerifier: string;
  state: string;
  redirectUri: string;
}

export async function buildAuthorizationUrl(
  provider: string,
  clientId: string,
  authority: string,
  scopes: string[],
  redirectUri: string,
): Promise<string> {
  const codeVerifier = randomBase64Url(48);
  const codeChallenge = await sha256Base64Url(codeVerifier);
  const state = randomBase64Url(16);

  // Persist PKCE state so the callback page can retrieve it
  const pkceState: PkceState = { provider, codeVerifier, state, redirectUri };
  sessionStorage.setItem(PKCE_STATE_KEY, JSON.stringify(pkceState));

  // Build the authorization endpoint from the authority
  const authBase = authority.includes('microsoftonline')
    ? `${authority.replace(/\/v2\.0$/, '')}/oauth2/v2.0/authorize`
    : authority.includes('google')
    ? 'https://accounts.google.com/o/oauth2/v2/auth'
    : `${authority.replace(/\/$/, '')}/v1/authorize`; // Okta / generic OIDC

  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    redirect_uri: redirectUri,
    scope: scopes.join(' ') || 'openid profile email',
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    response_mode: 'query',
  });

  return `${authBase}?${params}`;
}

export function getDefaultRedirectUri(): string {
  // PUBLIC_URL is baked in at build time ("/lens" in the combined image,
  // "" in local dev) so the OAuth redirect lands back under the app's base path.
  return `${window.location.origin}${process.env.PUBLIC_URL || ''}/auth/callback`;
}

export function retrievePkceState(): PkceState | null {
  const raw = sessionStorage.getItem(PKCE_STATE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PkceState;
  } catch {
    return null;
  }
}

export function clearPkceState(): void {
  sessionStorage.removeItem(PKCE_STATE_KEY);
}

// ── JWT helpers ───────────────────────────────────────────────────────────────

export function decodeJwtPayload(token: string): Record<string, any> | null {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return true;
  return payload.exp * 1000 < Date.now();
}

const AUTH_TOKEN_KEY = 'authToken';
const AUTH_USER_KEY = 'authUser';

export function storeAuth(token: string, user: any): void {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
}

export function getStoredToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function getStoredUser(): any | null {
  const raw = localStorage.getItem(AUTH_USER_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

export function clearAuth(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
}

// ── CloudForge single logout ──────────────────────────────────────────────────
// Same-origin contract: CloudLens is served at https://cloudforge.cloud/lens —
// the SAME origin as the CloudForge app. Two things follow:
//   (a) localStorage is shared with CloudForge, whose JWT lives under
//       'auth_token' (user object under 'auth_user' — the keys its own
//       clearSession/auth flows use), distinct from Lens's 'authToken'/'authUser';
//   (b) fetch('/api/auth/logout') resolves same-origin to the CloudForge backend
//       (the ALB routes /api/* there) and the httpOnly cf_auth cookie rides
//       along with credentials:'include'.
const CLOUDFORGE_TOKEN_KEY = 'auth_token';
const CLOUDFORGE_USER_KEY = 'auth_user';
const EMBEDDED_FLAG_KEY = 'cloudlens_embedded';

/**
 * Single logout for sessions that arrived via the CloudForge handoff (the
 * 'cloudlens_embedded' sessionStorage flag is set during the cf_token
 * exchange). Clears CloudForge's tokens from the shared localStorage,
 * best-effort POSTs its logout endpoint to clear the httpOnly cf_auth cookie,
 * then navigates to CloudForge's login page.
 *
 * Returns true when it took over navigation (embedded session); false for
 * standalone sessions, whose sign-out behavior is unchanged (caller shows the
 * normal Lens landing/login view).
 */
export function cloudForgeSingleLogout(): boolean {
  let embedded = false;
  try {
    embedded = sessionStorage.getItem(EMBEDDED_FLAG_KEY) === 'true';
  } catch { /* sessionStorage unavailable — treat as standalone */ }
  if (!embedded) return false;

  // Read CloudForge's JWT before removing it so the logout call can be authenticated.
  let cfToken: string | null = null;
  try {
    cfToken = localStorage.getItem(CLOUDFORGE_TOKEN_KEY);
    localStorage.removeItem(CLOUDFORGE_TOKEN_KEY);
    localStorage.removeItem(CLOUDFORGE_USER_KEY);
  } catch { /* ignore */ }

  // Best-effort: clear CloudForge's httpOnly cf_auth cookie via its backend
  // (POST /api/auth/logout, anonymous-safe). keepalive lets the request outlive
  // the navigation below; never block sign-out on its outcome.
  try {
    void fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: cfToken ? { Authorization: `Bearer ${cfToken}` } : undefined,
    }).catch(() => { /* ignore */ });
  } catch { /* ignore */ }

  try {
    sessionStorage.removeItem(EMBEDDED_FLAG_KEY);
  } catch { /* ignore */ }

  // Land on CloudForge's login page (not Lens's own login view).
  window.location.href = '/auth/login';
  return true;
}

export function isAuthenticated(): boolean {
  const token = getStoredToken();
  return !!token && !isTokenExpired(token);
}
